import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import { 
  Upload, 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Trash2,
  Lock,
  ChevronRight,
  Loader2,
  RefreshCw,
  Download,
  FileSpreadsheet,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status step configuration - updated for new workflow
const STATUS_STEPS = [
  { key: 'RECEIVED', label: 'Received', icon: Upload },
  { key: 'PROCESSING', label: 'Processing', icon: Clock },
  { key: 'APPROVED_INSTALLED', label: 'Approved & Installed', icon: CheckCircle2 }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    RECEIVED: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Upload, label: 'Received' },
    PROCESSING: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Processing' },
    PRE_PROCESSED: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Processing' },
    APPROVED_INSTALLED: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Approved & Installed' },
    APPROVED: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Approved' },
    REJECTED: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Rejected' }
  };
  
  const config = statusConfig[status] || statusConfig.RECEIVED;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

// Progress tracker component - updated for new status workflow
const StatusProgressTracker = ({ currentStatus }) => {
  // Map old statuses to new
  const normalizedStatus = currentStatus === 'PRE_PROCESSED' ? 'PROCESSING' : 
                           currentStatus === 'APPROVED' ? 'APPROVED_INSTALLED' : currentStatus;
  
  const currentIndex = STATUS_STEPS.findIndex(s => s.key === normalizedStatus);
  const isRejected = currentStatus === 'REJECTED';
  
  return (
    <div className="flex items-center gap-2 py-3">
      {STATUS_STEPS.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = !isRejected && index <= currentIndex;
        const isCurrent = !isRejected && index === currentIndex;
        
        return (
          <div key={step.key} className="flex items-center">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all ${
              isCompleted 
                ? 'bg-[#0891b2]/10 text-[#0891b2]' 
                : 'bg-gray-100 text-gray-400'
            } ${isCurrent ? 'ring-2 ring-[#0891b2]/30' : ''}`}>
              <Icon className={`h-3.5 w-3.5 ${isCompleted ? 'text-[#0891b2]' : ''}`} />
              <span className="text-xs font-medium">{step.label}</span>
            </div>
            {index < STATUS_STEPS.length - 1 && (
              <ChevronRight className={`h-4 w-4 mx-1 ${isCompleted ? 'text-[#0891b2]' : 'text-gray-300'}`} />
            )}
          </div>
        );
      })}
      {isRejected && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-100 text-red-700 ml-2">
          <XCircle className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">Rejected</span>
        </div>
      )}
    </div>
  );
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function CustomModelManager() {
  const [models, setModels] = useState([]);
  const [quota, setQuota] = useState({ sgr_remaining: 2, fcr_remaining: 2, sgr_used: 0, fcr_used: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  
  // File upload ref and state
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    model_type: 'SGR',
    model_name: '',
    species: 'Seabream',
    description: ''
  });

  const { token } = useAuth();
  const authToken = token;

  // Fetch user's custom models
  const fetchModels = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/custom-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch custom models:', error);
      toast.error('Failed to load custom models');
    }
  }, [token]);

  // Fetch quota
  const fetchQuota = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/custom-models/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuota(response.data);
    } catch (error) {
      console.error('Failed to fetch quota:', error);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchModels(), fetchQuota()]);
      setLoading(false);
    };
    loadData();
  }, [fetchModels, fetchQuota]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Only Excel files (.xlsx, .xls) are allowed');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Handle upload with file
  const handleUpload = async () => {
    if (!uploadForm.model_name.trim()) {
      toast.error('Please enter a model name');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select an Excel file to upload');
      return;
    }

    // Check quota
    const remaining = uploadForm.model_type === 'SGR' ? quota.sgr_remaining : quota.fcr_remaining;
    if (remaining <= 0) {
      toast.error(`You have used your free ${uploadForm.model_type} model uploads. Please upgrade to upload additional models.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('model_type', uploadForm.model_type);
      formData.append('model_name', uploadForm.model_name.trim());
      formData.append('species', uploadForm.species);
      formData.append('description', uploadForm.description || '');
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/custom-models/upload-file`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success(response.data.message || 'Model submitted for review');
      setShowUploadModal(false);
      setUploadForm({ model_type: 'SGR', model_name: '', species: 'Seabream', description: '' });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh data
      await Promise.all([fetchModels(), fetchQuota()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload model');
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!modelToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/custom-models/${modelToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Model deleted');
      setShowDeleteModal(false);
      setModelToDelete(null);
      
      // Refresh data
      await Promise.all([fetchModels(), fetchQuota()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete model');
    } finally {
      setDeleting(false);
    }
  };

  // Handle file download
  const handleDownload = async (model) => {
    if (!model.file_name) {
      toast.error('No file attached to this model');
      return;
    }

    setDownloadingId(model.id);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/custom-models/${model.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', model.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if upload is blocked (for specific model type)
  const isUploadBlocked = (type) => {
    return type === 'SGR' ? quota.sgr_remaining <= 0 : quota.fcr_remaining <= 0;
  };

  // Check if model can be deleted (not approved/installed)
  const canDelete = (status) => {
    return !['APPROVED', 'APPROVED_INSTALLED'].includes(status);
  };

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-white to-cyan-50/30" data-testid="custom-model-manager">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg text-[#0891b2] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Custom Model Manager (SGR / FCR)
            </CardTitle>
            <CardDescription className="text-gray-600 mt-1">
              Upload your own growth or feed models as Excel files for use in analyses
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => Promise.all([fetchModels(), fetchQuota()])}
              disabled={loading}
              data-testid="refresh-models-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-[#0891b2] hover:bg-[#0e7490]"
              data-testid="upload-model-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Model
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Quota Status */}
        <div className="flex gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-1">SGR Models</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${quota.sgr_remaining > 0 ? 'bg-[#0891b2]' : 'bg-red-500'}`}
                  style={{ width: `${(quota.sgr_used / 2) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {quota.sgr_used}/2 used
              </span>
            </div>
          </div>
          <div className="w-px bg-slate-200" />
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-1">FCR Models</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${quota.fcr_remaining > 0 ? 'bg-[#10b981]' : 'bg-red-500'}`}
                  style={{ width: `${(quota.fcr_used / 2) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {quota.fcr_used}/2 used
              </span>
            </div>
          </div>
        </div>

        {/* Models Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#0891b2]" />
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
            <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No custom models uploaded yet</p>
            <p className="text-gray-400 text-xs mt-1">Upload your SGR or FCR models as Excel files</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="models-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Model Name</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Species</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Upload Date</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">File</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr 
                    key={model.id} 
                    className={`border-b border-slate-100 hover:bg-white/80 transition-colors ${
                      model.status === 'APPROVED_INSTALLED' || model.status === 'APPROVED' ? 'bg-green-50/30' :
                      model.status === 'REJECTED' ? 'bg-red-50/30' : ''
                    }`}
                    data-testid={`model-row-${model.id}`}
                  >
                    <td className="py-3 px-3">
                      <p className="font-medium text-gray-800">{model.model_name}</p>
                      {model.description && (
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" title={model.description}>
                          {model.description}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        model.model_type === 'SGR' ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {model.model_type}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-gray-600">{model.species || 'Seabream'}</td>
                    <td className="py-3 px-3 text-gray-500 text-xs">{formatDate(model.submitted_at)}</td>
                    <td className="py-3 px-3">
                      {model.file_name ? (
                        <div className="flex items-center gap-1">
                          <FileSpreadsheet className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-xs text-gray-600 truncate max-w-[100px]" title={model.file_name}>
                            {model.file_name}
                          </span>
                          {model.file_size && (
                            <span className="text-xs text-gray-400">({formatFileSize(model.file_size)})</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No file</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={model.status} />
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        {model.file_name && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(model)}
                            disabled={downloadingId === model.id}
                            className="h-8 w-8 p-0 text-gray-500 hover:text-[#0891b2]"
                            title="Download file"
                            data-testid={`download-model-${model.id}`}
                          >
                            {downloadingId === model.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        {canDelete(model.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setModelToDelete(model);
                              setShowDeleteModal(true);
                            }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete model"
                            data-testid={`delete-model-${model.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Status Progress Legend */}
        {models.length > 0 && (
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-gray-500 mb-2">Status Workflow:</p>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-center">
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-200">
                      <Icon className="h-3 w-3 text-gray-500" />
                      <span className="text-xs text-gray-600">{step.label}</span>
                    </div>
                    {index < STATUS_STEPS.length - 1 && (
                      <ChevronRight className="h-3 w-3 mx-1 text-gray-300" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Help & Benefits Section */}
        <div className="mt-4">
          <HelpBenefits 
            youtubeUrl={HELP_URLS.customModelUpload.youtube}
            benefitsUrl={HELP_URLS.customModelUpload.benefits}
            featureName="Custom Model Upload"
          />
        </div>
      </CardContent>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => {
        setShowUploadModal(open);
        if (!open) {
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0891b2]">
              <Upload className="h-5 w-5" />
              Upload Custom Model (Excel)
            </DialogTitle>
            <DialogDescription>
              Submit your SGR or FCR model as an Excel file (.xlsx) for TPS review and approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Model Type */}
            <div className="space-y-2">
              <Label>Model Type *</Label>
              <Select
                value={uploadForm.model_type}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, model_type: value }))}
              >
                <SelectTrigger data-testid="model-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SGR" disabled={isUploadBlocked('SGR')}>
                    SGR (Growth Model) {isUploadBlocked('SGR') && '- Limit Reached'}
                  </SelectItem>
                  <SelectItem value="FCR" disabled={isUploadBlocked('FCR')}>
                    FCR (Feed Model) {isUploadBlocked('FCR') && '- Limit Reached'}
                  </SelectItem>
                </SelectContent>
              </Select>
              {isUploadBlocked(uploadForm.model_type) && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  <Lock className="h-4 w-4" />
                  <span>You have used your 2 free {uploadForm.model_type} model uploads. Please upgrade to upload additional models.</span>
                </div>
              )}
            </div>

            {/* Model Name */}
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input
                placeholder="e.g., My Custom SGR Model v1"
                value={uploadForm.model_name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, model_name: e.target.value }))}
                data-testid="model-name-input"
              />
            </div>

            {/* Species */}
            <div className="space-y-2">
              <Label>Species</Label>
              <Select
                value={uploadForm.species}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, species: value }))}
              >
                <SelectTrigger data-testid="model-species-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Seabream">Seabream</SelectItem>
                  <SelectItem value="Tilapia">Tilapia</SelectItem>
                  <SelectItem value="Salmon">Salmon</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Excel File (.xlsx) *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#0891b2] transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  {selectedFile ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <FileSpreadsheet className="h-8 w-8" />
                      <div>
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to select file</p>
                      <p className="text-xs text-gray-400">Only .xlsx or .xls files</p>
                    </>
                  )}
                </label>
              </div>
              {selectedFile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-red-500 text-xs"
                >
                  Remove file
                </Button>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Describe your model, its parameters, or source data..."
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                className="resize-none"
                rows={2}
                data-testid="model-description-input"
              />
            </div>

            {/* Info Note */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">TPS Review Required</p>
                <p className="text-xs mt-1">Your model will be reviewed and processed by the TPS team. Once approved and installed, it will become available for use in your analyses.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || isUploadBlocked(uploadForm.model_type) || !selectedFile}
              className="bg-[#0891b2] hover:bg-[#0e7490]"
              data-testid="submit-model-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Model
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Model
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{modelToDelete?.model_name}"? This will also remove the uploaded file. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-model-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Model'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
